import type { Change, Changelog } from '../../shared/types/changelog';

// Per-tenant relevance filtering. Kept pure (no Vue, no config import, no IndexedDB)
// like semver.ts / changelogDiff.ts, so it is unit-testable in happy-dom AND importable
// by the build-time `changelog-filter` module (which erases the type import above and
// runs this in node). The tenant context is passed in; this file never reaches the
// config layer itself.

export interface ChangelogTenantContext {
	/** Active tenant productType, e.g. 'towing'. Undefined before config resolves. */
	productType: string | undefined;
	/** Active tenant portalType, e.g. 'customer'. Undefined before config resolves. */
	portalType: string | undefined;
	/** Config feature-flag predicate (useCustomerConfig.isFeatureEnabled at runtime,
	 *  or a raw config lookup at build time). */
	isFeatureEnabled: (featureId: string) => boolean;
}

// Shown for a release that has real changes but none relevant to this tenant. A release
// is never dropped for having nothing relevant (that would hide a version the tenant can
// otherwise see, and could hide the footer link on the running version); it collapses to
// this instead. Authored here so the build-baked and runtime-filtered results agree.
export const GENERIC_CHANGE: Change = {
	type: 'chore',
	message: 'General bug fixes and improvements',
};

// An absent/empty tag list imposes no constraint (the change is universal for that
// dimension). A non-empty list matches only when the tenant value is present in it.
function matchesList(list: readonly string[] | undefined, value: string | undefined): boolean {
	if (!list || list.length === 0) return true;
	return value != null && list.includes(value);
}

// A change is shown to a tenant when every present constraint holds: product line (any
// of `products`), portal type (any of `portals`), and (any of) `features`. Untagged
// changes are always shown. Dimensions AND together; values within one OR together.
export function isChangeRelevant(change: Change, ctx: ChangelogTenantContext): boolean {
	if (!matchesList(change.products, ctx.productType)) return false;
	if (!matchesList(change.portals, ctx.portalType)) return false;
	if (change.features && change.features.length > 0) {
		if (!change.features.some((featureId) => ctx.isFeatureEnabled(featureId))) return false;
	}
	return true;
}

// Filter every release to the changes relevant to one tenant. Releases are kept even
// when nothing is relevant: an emptied release collapses to a single generic change so a
// version is never shown blank. Non-mutating: releases and changes are copied.
export function filterChangelogForTenant(
	changelog: Changelog,
	ctx: ChangelogTenantContext,
	fallback: Change = GENERIC_CHANGE,
): Changelog {
	const releases = changelog.releases.map((release) => {
		const changes = release.changes.filter((change) => isChangeRelevant(change, ctx));
		return { ...release, changes: changes.length > 0 ? changes : [fallback] };
	});
	return { releases };
}
